import { useMemo } from 'react';
import copy from 'copy-to-clipboard';
import toast from 'react-hot-toast';
import { useGetExternalCalibrationAuthQuery } from 'services/findemV2';

export const useCalibrationSelection = ({ jobTitle, userFullName, collectionId, jobId, job }) => {
  const { data: externalCalibrationAuth, isLoading: isLoadingToken } = useGetExternalCalibrationAuthQuery(
    { collectionId, jobId },
    { skip: !collectionId || !jobId },
  );

  // Generate calibration link - construct URL based on collection_id and job_slug (without token or sourcing_campaign_id)
  const calibrationLink = useMemo(() => {
    if (collectionId && job?.slug) {
      const baseUrl = `${window.location.origin}/app/shared-jobs-calibration/${collectionId}/${job.slug}`;
      return baseUrl;
    }
    return '[calibration_link]';
  }, [collectionId, job?.slug]);

  // Generate authenticated calibration link with token and sourcing_campaign_id
  const authenticatedCalibrationLink = useMemo(() => {
    if (calibrationLink && calibrationLink !== '[calibration_link]') {
      const url = new URL(calibrationLink);

      // Add token if available
      if (externalCalibrationAuth?.token) {
        url.searchParams.set('calibrate-token', externalCalibrationAuth.token);
      }

      // Add sourcing_campaign_id if available
      if (job?.sourcingCampaign?.id) {
        url.searchParams.set('sourcing_campaign_id', job.sourcingCampaign.id);
      }

      return url.toString();
    }
    return calibrationLink;
  }, [externalCalibrationAuth?.token, calibrationLink, job?.sourcingCampaign?.id]);

  const messageTemplate = `Hi,
We're using Getro agents to boost candidates for your role ${jobTitle}.

Could you do a quick calibration to help the agent better understand your needs and find stronger candidates?

It takes about 3 minutes, and you don't have to create an account.

Start calibration here: ${calibrationLink}

Thanks,
${userFullName}`;

  // Create message with full authenticated link for display
  const messageWithFullLink = useMemo(() => {
    if (authenticatedCalibrationLink && authenticatedCalibrationLink !== '[calibration_link]') {
      // Replace the base link with the authenticated link in the message template
      const urlRegex = /(https?:\/\/[^\s]+)/;
      return messageTemplate.replace(urlRegex, authenticatedCalibrationLink);
    }
    return messageTemplate;
  }, [messageTemplate, authenticatedCalibrationLink]);

  const handleCopyMessage = () => {
    // Replace the base link with authenticated link when copying
    const messageWithAuthLink = messageTemplate.replace(calibrationLink, authenticatedCalibrationLink);
    copy(messageWithAuthLink);
    toast('Message copied!', { icon: 'success', id: 'copy-calibration-message' });
  };

  const handleCopyLink = () => {
    copy(authenticatedCalibrationLink);
    toast('Link copied!', { icon: 'success', id: 'copy-calibration-link' });
  };

  const handleOpenCalibrationLink = () => {
    window.open(authenticatedCalibrationLink, '_blank', 'noopener,noreferrer');
  };

  return {
    handleCopyMessage,
    handleCopyLink,
    handleOpenCalibrationLink,
    messageWithFullLink,
    isLoadingToken,
  };
};
